import React, { useState, useCallback } from 'react'
import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react'
import { Plus, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@deepguide-ai/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@deepguide-ai/ui'
import { Separator } from '@deepguide-ai/ui'
import {
  SettingsField,
  SettingsPopoverHeader,
} from '@/tiptap/tiptap-ui/settings-popover/settings-popover'
import { CardGroupAttributes } from './card-group-extension'

interface CardGroupNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

export function CardGroupNodeView({
  node,
  editor,
  getPos,
  updateAttributes,
  deleteNode,
  selected,
}: CardGroupNodeViewProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [tempCols, setTempCols] = useState(node.attrs.cols || 2)

  const attrs = node.attrs as CardGroupAttributes
  const cols = attrs.cols || 2

  // Handle add card
  const handleAddCard = useCallback(() => {
    const pos = getPos()
    if (pos === undefined) return

    const newCardPos = pos + node.nodeSize - 1 // Position at the end of cards

    editor.commands.insertContentAt(newCardPos, {
      type: 'Card',
      attrs: {
        title: 'New Card',
      },
      content: [
        {
          type: 'paragraph',
          content: [],
        },
      ],
    })
  }, [editor, getPos, node.nodeSize])

  // Handle delete card group
  const handleDeleteCardGroup = useCallback(() => {
    deleteNode()
  }, [deleteNode])

  // Handle save settings
  const handleSaveSettings = useCallback(() => {
    if (tempCols !== cols) {
      updateAttributes({ cols: tempCols })
    }
    setIsPopoverOpen(false)
  }, [tempCols, cols, updateAttributes])

  // Handle cancel settings
  const handleCancelSettings = useCallback(() => {
    setTempCols(cols)
    setIsPopoverOpen(false)
  }, [cols])

  // Handle open settings
  const handleOpenSettings = useCallback(() => {
    setTempCols(cols)
    setIsPopoverOpen(true)
  }, [cols])

  return (
    <NodeViewWrapper
      className={cn(
        'group/cardgroup relative my-5 p-0.5',
        selected && 'ProseMirror-selectednode'
      )}
    >
      <div className='card-group-container relative'>
        {/* Action buttons - appears on group hover */}
        {editor.isEditable && (
          <div
            className={`absolute top-[-5px] -right-[65px] z-10 flex h-full items-start gap-0.5 p-1 pl-4 opacity-0 transition-opacity group-hover/cardgroup:opacity-100 ${
              isPopoverOpen ? 'opacity-100' : ''
            }`}
            contentEditable={false}
          >
            <Button variant='ghost' size='menuAction' onClick={handleAddCard}>
              <Plus className='text-foreground h-4 w-4' />
            </Button>
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='ghost'
                  size='menuAction'
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOpenSettings()
                  }}
                >
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </PopoverTrigger>

              <PopoverContent
                className='w-80 p-4 pb-3'
                align='end'
                side='bottom'
                sideOffset={8}
              >
                <div className='space-y-4'>
                  <SettingsPopoverHeader
                    title='Card Group Settings'
                    onClose={handleCancelSettings}
                  />

                  <SettingsField label='Number of Columns'>
                    <div className='grid grid-cols-4 gap-2'>
                      {[1, 2, 3, 4].map((num) => (
                        <button
                          key={num}
                          type='button'
                          onClick={() => setTempCols(num)}
                          className={cn(
                            'cursor-pointer rounded-sm border px-3 py-2 text-sm transition-colors',
                            tempCols === num
                              ? 'border-primary bg-primary text-background'
                              : 'border-border bg-background text-foreground hover:bg-accent'
                          )}
                        >
                          {num === 1
                            ? 'One'
                            : num === 2
                              ? 'Two'
                              : num === 3
                                ? 'Three'
                                : 'Four'}
                        </button>
                      ))}
                    </div>
                  </SettingsField>
                  <Separator className='mt-5 mb-3 -ml-4 !w-[calc(100%+32px)]' />

                  <div className='flex items-center justify-between'>
                    <Button
                      variant='destructive'
                      className='h-8 !bg-transparent px-2 py-1.5 text-[var(--destructive)] shadow-none'
                      onClick={handleDeleteCardGroup}
                    >
                      Delete
                    </Button>
                    <div className='flex items-center space-x-2'>
                      <Button
                        variant='outline'
                        className='h-8 border-[var(--border)] px-2 py-1.5'
                        onClick={handleCancelSettings}
                      >
                        Cancel
                      </Button>
                      <Button
                        className='h-8 px-2 py-1.5'
                        onClick={handleSaveSettings}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Grid of cards */}
        <div
          className={cn(
            'card-group-grid grid gap-4',
            cols === 1
              ? 'grid-cols-1'
              : cols === 2
                ? 'grid-cols-1 sm:grid-cols-2'
                : cols === 3
                  ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
                  : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
          )}
        >
          <NodeViewContent />
        </div>
      </div>
    </NodeViewWrapper>
  )
}
